import { useCallback, useEffect, useRef, useState } from "react";
import browserHistory from "../../browserHistory";

type LeaveAction = (() => void) | null;

const PROTECTED_APPLICATION_STATUSES = new Set([1, 2, 14]);

export const shouldProtectApplicantLeave = ({
  applicationStatus,
  disabled,
  isApplicant,
}: {
  applicationStatus: unknown;
  disabled: boolean;
  isApplicant: boolean;
}) =>
  isApplicant &&
  !disabled &&
  PROTECTED_APPLICATION_STATUSES.has(Number(applicationStatus));

const isSameLocation = (left: any, right: any) =>
  left?.pathname === right?.pathname &&
  left?.search === right?.search &&
  left?.hash === right?.hash;

export const useLeaveProtection = (enabled: boolean) => {
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState(false);
  const pendingTransitionRef = useRef<any>(null);
  const pendingLeaveActionRef = useRef<LeaveAction>(null);
  const unblockRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    if (!enabled) {
      pendingTransitionRef.current = null;
      pendingLeaveActionRef.current = null;
      setIsLeavePromptOpen(false);
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      return undefined;
    }

    const unblock = browserHistory.block((transition: any) => {
      if (isSameLocation(transition?.location, browserHistory.location)) {
        return;
      }

      pendingTransitionRef.current = transition;
      setIsLeavePromptOpen(true);
    });

    unblockRef.current = unblock;

    return () => {
      unblock();
      unblockRef.current = null;
      pendingTransitionRef.current = null;
      pendingLeaveActionRef.current = null;
      setIsLeavePromptOpen(false);
    };
  }, [enabled]);

  const queueLeaveAction = useCallback((action: LeaveAction) => {
    pendingLeaveActionRef.current = action;
  }, []);

  const cancelLeave = useCallback(() => {
    pendingTransitionRef.current = null;
    pendingLeaveActionRef.current = null;
    setIsLeavePromptOpen(false);
  }, []);

  const confirmLeave = useCallback(() => {
    const transition = pendingTransitionRef.current;
    const pendingLeaveAction = pendingLeaveActionRef.current;

    pendingTransitionRef.current = null;
    pendingLeaveActionRef.current = null;
    setIsLeavePromptOpen(false);

    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    pendingLeaveAction?.();
    transition?.retry?.();
  }, []);

  return {
    cancelLeave,
    confirmLeave,
    isLeavePromptOpen,
    queueLeaveAction,
  };
};
